/**
 * DP / Folha — Fase 2.3: Medidas Disciplinares + Histórico Funcional
 * Aba da página DPFolhaConfigPage
 */

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import Autocomplete from '../ui/autocomplete';
import { formatCPF } from '../../lib/helpers';
import {
  Gavel, History, Plus, Pencil, Trash2, Download, Upload, FileSignature,
  AlertTriangle, CheckCircle2, Loader2, Clock, ArrowRight, Filter, User
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtDate = (s) => s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '—';

const TIPOS_MEDIDA = [
  { v: 'advertencia_verbal', l: 'Advertência Verbal', color: 'bg-slate-500', icon: '⚠️' },
  { v: 'advertencia_escrita', l: 'Advertência Escrita', color: 'bg-amber-500', icon: '📝' },
  { v: 'suspensao', l: 'Suspensão', color: 'bg-orange-500', icon: '⏸️' },
  { v: 'justa_causa', l: 'Justa Causa', color: 'bg-red-600', icon: '🚫' },
];

const TIPOS_HISTORICO = [
  { v: 'admissao', l: 'Admissão', esocial: 'S-2200', color: 'bg-emerald-500' },
  { v: 'promocao', l: 'Promoção', esocial: 'S-2206', color: 'bg-indigo-500' },
  { v: 'alteracao_cargo', l: 'Alteração de Cargo', esocial: 'S-2206', color: 'bg-indigo-400' },
  { v: 'alteracao_salarial', l: 'Alteração Salarial', esocial: 'S-2206', color: 'bg-blue-500' },
  { v: 'alteracao_jornada', l: 'Alteração de Jornada', esocial: 'S-2206', color: 'bg-sky-500' },
  { v: 'alteracao_contratual', l: 'Alteração Contratual', esocial: 'S-2206', color: 'bg-cyan-500' },
  { v: 'alteracao_cadastral', l: 'Alteração Cadastral', esocial: 'S-2205', color: 'bg-cyan-400' },
  { v: 'transferencia_filial', l: 'Transferência de Filial', esocial: 'S-2206', color: 'bg-teal-500' },
  { v: 'afastamento', l: 'Afastamento', esocial: 'S-2230', color: 'bg-amber-500' },
  { v: 'retorno_afastamento', l: 'Retorno de Afastamento', esocial: 'S-2230', color: 'bg-lime-500' },
  { v: 'aviso_previo', l: 'Aviso Prévio', esocial: 'S-2250', color: 'bg-orange-500' },
  { v: 'desligamento', l: 'Desligamento', esocial: 'S-2299', color: 'bg-red-600' },
];

const tipoMedida = (v) => TIPOS_MEDIDA.find(t => t.v === v) || { l: v, color: 'bg-slate-400', icon: '•' };
const tipoHistorico = (v) => TIPOS_HISTORICO.find(t => t.v === v) || { l: v, color: 'bg-slate-400', esocial: '' };

// ============================================================================
// SUB-TAB: MEDIDAS DISCIPLINARES
// ============================================================================
const MedidasTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = {
    employee_id: '', employee_nome: '', tipo: 'advertencia_verbal', data_ocorrencia: '', data_aplicacao: '',
    motivo: '', descricao: '', dias_suspensao: '', testemunhas: [],
    aplicada_por_nome: '', aplicada_por_employee_id: '', observacoes: '',
  };
  const [form, setForm] = useState(empty);
  const [uploadFile, setUploadFile] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/medidas`, authHeader());
      setItems(r.data || []);
    } catch (e) { toast.error('Erro ao carregar medidas'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEdit(null); setForm(empty); setOpen(true); };
  const openEdit = (m) => {
    setEdit(m.id);
    setForm({ ...empty, ...m, dias_suspensao: m.dias_suspensao ?? '', testemunhas: m.testemunhas || [], aplicada_por_employee_id: m.aplicada_por_employee_id || '' });
    setOpen(true);
  };

  const save = async () => {
    if (!form.employee_id || !form.data_ocorrencia || !form.data_aplicacao || !form.motivo || !form.descricao) {
      return toast.error('Colaborador, datas, motivo e descrição são obrigatórios');
    }
    if (form.tipo === 'suspensao' && (!form.dias_suspensao || form.dias_suspensao < 1)) {
      return toast.error('Para suspensão, informe os dias');
    }
    try {
      const payload = {
        ...form,
        dias_suspensao: form.tipo === 'suspensao' ? parseInt(form.dias_suspensao) : null,
      };
      delete payload.employee_nome;
      if (edit) {
        await axios.put(`${API}/api/calculos-dp/medidas/${edit}`, payload, authHeader());
        toast.success('Medida atualizada');
      } else {
        await axios.post(`${API}/api/calculos-dp/medidas`, payload, authHeader());
        toast.success('Medida registrada');
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir esta medida disciplinar?')) return;
    try {
      await axios.delete(`${API}/api/calculos-dp/medidas/${id}`, authHeader());
      toast.success('Excluída'); load();
    } catch (e) { toast.error('Erro'); }
  };

  const uploadPdf = async (medidaId) => {
    const file = uploadFile[medidaId];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('arquivo', file);
      await axios.post(`${API}/api/calculos-dp/medidas/${medidaId}/upload-pdf`, fd, {
        ...authHeader(), headers: { ...authHeader().headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('PDF anexado');
      setUploadFile({ ...uploadFile, [medidaId]: null });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro upload'); }
  };

  const downloadPdf = (id) => {
    const token = localStorage.getItem('token');
    fetch(`${API}/api/calculos-dp/medidas/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(b => window.open(URL.createObjectURL(b), '_blank'))
      .catch(() => toast.error('Erro'));
  };

  const requestSig = async (id) => {
    try {
      await axios.post(`${API}/api/calculos-dp/medidas/${id}/request-signature`, {}, authHeader());
      toast.success('Assinatura solicitada'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  const markSigned = async (id, recusou = false) => {
    try {
      const fd = new FormData();
      fd.append('recusou', recusou ? 'true' : 'false');
      await axios.post(`${API}/api/calculos-dp/medidas/${id}/mark-signed`, fd, authHeader());
      toast.success(recusou ? 'Recusa registrada' : 'Marcado como assinado'); load();
    } catch (e) { toast.error('Erro'); }
  };

  const addTestemunha = () => setForm({ ...form, testemunhas: [...form.testemunhas, { nome: '', cpf: '', cargo: '', positions_id: '' }] });
  const updTestemunha = (i, k, v, k2, v2) => {
    const arr = [...form.testemunhas]; arr[i] = { ...arr[i], [k]: v };
    if (k2) arr[i][k2] = v2;
    setForm({ ...form, testemunhas: arr });
  };
  const delTestemunha = (i) => setForm({ ...form, testemunhas: form.testemunhas.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-600">{items.length} medida(s) registrada(s).</p>
        <Button onClick={openCreate} data-testid="dp-medida-novo-btn"><Plus className="h-4 w-4 mr-2" />Nova Medida</Button>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Colaborador</th>
              <th className="text-left p-3">Tipo</th>
              <th className="text-left p-3">Data Aplicação</th>
              <th className="text-left p-3">Motivo</th>
              <th className="text-left p-3">PDF</th>
              <th className="text-left p-3">Assinatura</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500"><Loader2 className="h-4 w-4 inline animate-spin mr-2" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500">Nenhuma medida registrada.</td></tr>
            ) : items.map((m) => {
              const t = tipoMedida(m.tipo);
              return (
                <tr key={m.id} className="border-t hover:bg-slate-50">
                  <td className="p-3"><div className="font-medium">{m.employee_nome}</div><div className="text-xs text-slate-500">{m.employee_cargo}</div></td>
                  <td className="p-3"><Badge className={`${t.color} text-white`}>{t.icon} {t.l}{m.tipo === 'suspensao' && m.dias_suspensao ? ` (${m.dias_suspensao}d)` : ''}</Badge></td>
                  <td className="p-3 text-xs">{fmtDate(m.data_aplicacao)}</td>
                  <td className="p-3 text-xs max-w-xs truncate" title={m.motivo}>{m.motivo}</td>
                  <td className="p-3">
                    {m.tem_pdf ? (
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(m.id)}><Download className="h-4 w-4" /></Button>
                    ) : (
                      <div className="flex gap-1 items-center">
                        <Input type="file" accept=".pdf" className="h-8 text-xs w-28" onChange={(e) => setUploadFile({ ...uploadFile, [m.id]: e.target.files?.[0] || null })} />
                        <Button size="sm" variant="ghost" onClick={() => uploadPdf(m.id)} disabled={!uploadFile[m.id]}><Upload className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    {m.assinatura_status === 'signed' && <Badge className="bg-emerald-500"><FileSignature className="h-3 w-3 mr-1" />Assinado</Badge>}
                    {m.assinatura_status === 'refused' && <Badge variant="destructive">Recusou</Badge>}
                    {m.assinatura_status === 'pending' && (
                      <>
                        <Badge className="bg-amber-500 mr-1"><Clock className="h-3 w-3 mr-1" />Pendente</Badge>
                        <Button size="sm" variant="ghost" onClick={() => markSigned(m.id, false)}>✓</Button>
                        <Button size="sm" variant="ghost" onClick={() => markSigned(m.id, true)} className="text-red-600">✗</Button>
                      </>
                    )}
                    {m.assinatura_status === 'none' && m.tem_pdf && (
                      <Button size="sm" variant="ghost" onClick={() => requestSig(m.id)}><FileSignature className="h-3 w-3 mr-1" />Solicitar</Button>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(m)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(m.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto" data-testid="dp-medida-dialog">
          <DialogHeader>
            <DialogTitle>{edit ? 'Editar Medida Disciplinar' : 'Nova Medida Disciplinar'}</DialogTitle>
            <DialogDescription>Registro formal de advertência, suspensão ou justa causa. Após salvar, faça upload do PDF da carta e (opcional) solicite assinatura do colaborador.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Colaborador *</Label>
              <Autocomplete
                value={form.employee_id}
                selectedLabel={form.employee_nome}
                onValueChange={(v, item) => setForm({ ...form, employee_id: v, employee_nome: item?.nome || '' })}
                endpoint={`${API}/api/employees-paged`}
                getOptionLabel={(e) => `${e.nome}${e.cargo ? ` — ${e.cargo}` : ''}`}
                getOptionValue={(e) => e.id}
                placeholder="Selecione um colaborador..."
              />
            </div>
            <div>
              <Label>Tipo *</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS_MEDIDA.map((t) => <SelectItem key={t.v} value={t.v}>{t.icon} {t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.tipo === 'suspensao' && (
              <div>
                <Label>Dias de Suspensão *</Label>
                <Input type="number" min={1} max={30} value={form.dias_suspensao} onChange={(e) => setForm({ ...form, dias_suspensao: e.target.value })} />
              </div>
            )}
            <div>
              <Label>Data da Ocorrência *</Label>
              <Input type="date" value={form.data_ocorrencia} onChange={(e) => setForm({ ...form, data_ocorrencia: e.target.value })} />
            </div>
            <div>
              <Label>Data de Aplicação *</Label>
              <Input type="date" value={form.data_aplicacao} onChange={(e) => setForm({ ...form, data_aplicacao: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Motivo (resumo) *</Label>
              <Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} placeholder="Ex.: Atraso reiterado, descumprimento de norma..." />
            </div>
            <div className="col-span-2">
              <Label>Descrição Detalhada *</Label>
              <Textarea rows={4} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Aplicada por (gestor)</Label>
              <Autocomplete
                value={form.aplicada_por_employee_id}
                selectedLabel={form.aplicada_por_nome}
                onValueChange={(v, item) => setForm({ ...form, aplicada_por_employee_id: v, aplicada_por_nome: item?.nome || '' })}
                endpoint={`${API}/api/employees-paged`}
                getOptionLabel={(e) => e.nome}
                getOptionValue={(e) => e.id}
                placeholder="Nome do gestor/RH que aplicou"
              />
            </div>
            <div className="col-span-2 mt-2">
              <div className="flex items-center justify-between mb-2">
                <Label>Testemunhas</Label>
                <Button type="button" size="sm" variant="outline" onClick={addTestemunha}><Plus className="h-3 w-3 mr-1" />Adicionar</Button>
              </div>
              {form.testemunhas.length === 0 ? <p className="text-xs text-slate-500 italic">Nenhuma testemunha registrada.</p> : form.testemunhas.map((t, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 border rounded bg-slate-50 mb-1">
                  <div className="col-span-5"><Label className="text-xs">Nome</Label><Input className="h-9" value={t.nome} onChange={(e) => updTestemunha(i, 'nome', e.target.value)} /></div>
                  <div className="col-span-2"><Label className="text-xs">CPF</Label><Input className="h-9" value={t.cpf || ''} onChange={(e) => updTestemunha(i, 'cpf', formatCPF(e.target.value.replace(/\D/g, '').slice(0, 11)))} /></div>
                  <div className="col-span-5"><Label className="text-xs">Cargo</Label>
                    <Autocomplete
                      value={t.positions_id}
                      selectedLabel={t.cargo}
                      onValueChange={(v, item) => updTestemunha(i, 'cargo', item?.name || v, 'positions_id', v)}
                      endpoint={`${API}/api/positions`}
                      getOptionLabel={(p) => p.name}
                      getOptionValue={(p) => p.id}
                      placeholder="Cargo"
                    />
                  </div>
                  <div className="col-span-1"><Button type="button" size="sm" variant="ghost" onClick={() => delTestemunha(i)}><Trash2 className="h-4 w-4 text-red-600" /></Button></div>
                </div>
              ))}
            </div>
            <div className="col-span-2">
              <Label>Observações</Label>
              <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="dp-medida-salvar-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// SUB-TAB: HISTÓRICO FUNCIONAL (TIMELINE)
// ============================================================================
const HistoricoTab = () => {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEmp, setFiltroEmp] = useState('');
  const [open, setOpen] = useState(false);
  const empty = {
    employee_id: '', tipo: 'admissao', data_evento: '',
    motivo: '', justificativa: '', valores_anteriores: {}, valores_novos: {},
  };
  const [form, setForm] = useState(empty);
  const [paresAnt, setParesAnt] = useState([{ k: '', v: '' }]);
  const [paresNov, setParesNov] = useState([{ k: '', v: '' }]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroEmp) params.set('employee_id', filtroEmp);
      const [r, re] = await Promise.all([
        axios.get(`${API}/api/calculos-dp/historico?${params}`, authHeader()),
        axios.get(`${API}/api/employees`, authHeader()),
      ]);
      setItems(r.data || []);
      setEmployees(re.data || []);
    } catch (e) { toast.error('Erro ao carregar histórico'); }
    finally { setLoading(false); }
  }, [filtroEmp]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(empty);
    setParesAnt([{ k: '', v: '' }]);
    setParesNov([{ k: '', v: '' }]);
    setOpen(true);
  };

  const pairsToObj = (pairs) => {
    const o = {};
    pairs.forEach(p => { if (p.k && p.v !== '') o[p.k] = p.v; });
    return o;
  };

  const save = async () => {
    if (!form.employee_id || !form.data_evento) {
      return toast.error('Colaborador e data são obrigatórios');
    }
    try {
      const payload = {
        ...form,
        valores_anteriores: pairsToObj(paresAnt),
        valores_novos: pairsToObj(paresNov),
      };
      await axios.post(`${API}/api/calculos-dp/historico`, payload, authHeader());
      toast.success('Evento registrado');
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir este evento do histórico?')) return;
    try {
      await axios.delete(`${API}/api/calculos-dp/historico/${id}`, authHeader());
      toast.success('Excluído'); load();
    } catch (e) { toast.error('Erro'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-center">
        <div className="flex-1 flex gap-2 items-center">
          <Filter className="h-4 w-4 text-slate-500" />
          <Select value={filtroEmp} onValueChange={setFiltroEmp}>
            <SelectTrigger className="w-72"><SelectValue placeholder="Filtrar por colaborador..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os colaboradores</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}
            </SelectContent>
          </Select>
          {filtroEmp && filtroEmp !== 'todos' && (
            <Button size="sm" variant="ghost" onClick={() => setFiltroEmp('')}>Limpar</Button>
          )}
        </div>
        <Button onClick={openCreate} data-testid="dp-historico-novo-btn"><Plus className="h-4 w-4 mr-2" />Novo Evento</Button>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="p-6 text-center text-slate-500"><Loader2 className="h-5 w-5 inline animate-spin mr-2" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-6 text-center text-slate-500">Nenhum evento de histórico cadastrado{filtroEmp ? ' para este colaborador.' : '.'}</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map((h) => {
            const t = tipoHistorico(h.tipo);
            const antKeys = Object.keys(h.valores_anteriores || {});
            const novKeys = Object.keys(h.valores_novos || {});
            return (
              <Card key={h.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-full ${t.color} rounded-l-md min-h-[60px] -my-3 -ml-3`}></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`${t.color} text-white`}>{t.l}</Badge>
                        {h.esocial_evento_codigo && <Badge variant="outline" className="font-mono text-xs">{h.esocial_evento_codigo}</Badge>}
                        {h.esocial_enviado && <Badge className="bg-emerald-500 text-xs">eSocial enviado</Badge>}
                        <span className="text-sm font-medium ml-auto">{fmtDate(h.data_evento)}</span>
                      </div>
                      <div className="mt-1 text-sm font-medium flex items-center gap-2">
                        <User className="h-3 w-3" /> {h.employee_nome}
                      </div>
                      {h.motivo && <div className="mt-1 text-sm text-slate-700">{h.motivo}</div>}
                      {h.justificativa && <div className="mt-1 text-xs text-slate-500 italic">"{h.justificativa}"</div>}

                      {(antKeys.length > 0 || novKeys.length > 0) && (
                        <div className="mt-2 flex items-center gap-3 text-xs">
                          {antKeys.length > 0 && (
                            <div className="bg-red-50 border border-red-200 rounded px-2 py-1">
                              <div className="text-red-700 font-semibold mb-0.5">Antes</div>
                              {antKeys.map(k => <div key={k}><span className="text-slate-500">{k}:</span> {String(h.valores_anteriores[k])}</div>)}
                            </div>
                          )}
                          <ArrowRight className="h-4 w-4 text-slate-400" />
                          {novKeys.length > 0 && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                              <div className="text-emerald-700 font-semibold mb-0.5">Depois</div>
                              {novKeys.map(k => <div key={k}><span className="text-slate-500">{k}:</span> {String(h.valores_novos[k])}</div>)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => remove(h.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="dp-historico-dialog">
          <DialogHeader>
            <DialogTitle>Novo Evento — Histórico Funcional</DialogTitle>
            <DialogDescription>Registra alteração contratual/cadastral para fins de eSocial (S-2200/S-2205/S-2206/S-2230/S-2299).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Colaborador *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.nome} — {e.cargo}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo de Evento *</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS_HISTORICO.map((t) => <SelectItem key={t.v} value={t.v}>{t.l} ({t.esocial})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data do Evento *</Label>
              <Input type="date" value={form.data_evento} onChange={(e) => setForm({ ...form, data_evento: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Motivo</Label>
              <Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} placeholder="Resumo do motivo..." />
            </div>
            <div className="col-span-2">
              <Label>Justificativa / Observação</Label>
              <Textarea value={form.justificativa} onChange={(e) => setForm({ ...form, justificativa: e.target.value })} />
            </div>

            {/* Snapshot de alterações */}
            <div className="col-span-2 grid grid-cols-2 gap-2 mt-2">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-red-700">Valores Anteriores</CardTitle></CardHeader>
                <CardContent className="space-y-1">
                  {paresAnt.map((p, i) => (
                    <div key={i} className="flex gap-1">
                      <Input className="h-8 text-xs" placeholder="campo (ex.: salario)" value={p.k} onChange={(e) => { const a = [...paresAnt]; a[i].k = e.target.value; setParesAnt(a); }} />
                      <Input className="h-8 text-xs" placeholder="valor" value={p.v} onChange={(e) => { const a = [...paresAnt]; a[i].v = e.target.value; setParesAnt(a); }} />
                      <Button size="sm" variant="ghost" onClick={() => setParesAnt(paresAnt.filter((_, idx) => idx !== i))}><Trash2 className="h-3 w-3 text-red-600" /></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setParesAnt([...paresAnt, { k: '', v: '' }])} className="w-full">+ Campo</Button>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-emerald-700">Valores Novos</CardTitle></CardHeader>
                <CardContent className="space-y-1">
                  {paresNov.map((p, i) => (
                    <div key={i} className="flex gap-1">
                      <Input className="h-8 text-xs" placeholder="campo" value={p.k} onChange={(e) => { const a = [...paresNov]; a[i].k = e.target.value; setParesNov(a); }} />
                      <Input className="h-8 text-xs" placeholder="valor" value={p.v} onChange={(e) => { const a = [...paresNov]; a[i].v = e.target.value; setParesNov(a); }} />
                      <Button size="sm" variant="ghost" onClick={() => setParesNov(paresNov.filter((_, idx) => idx !== i))}><Trash2 className="h-3 w-3 text-red-600" /></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setParesNov([...paresNov, { k: '', v: '' }])} className="w-full">+ Campo</Button>
                </CardContent>
              </Card>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="dp-historico-salvar-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
const DisciplinarHistoricoTab = () => {
  return (
    <Tabs defaultValue="medidas" className="w-full">
      <TabsList className="grid grid-cols-2 mb-4">
        <TabsTrigger value="medidas" data-testid="dp-disc-tab-medidas"><Gavel className="h-4 w-4 mr-1" />Medidas Disciplinares</TabsTrigger>
        <TabsTrigger value="historico" data-testid="dp-disc-tab-historico"><History className="h-4 w-4 mr-1" />Histórico Funcional</TabsTrigger>
      </TabsList>
      <TabsContent value="medidas"><MedidasTab /></TabsContent>
      <TabsContent value="historico"><HistoricoTab /></TabsContent>
    </Tabs>
  );
};

export default DisciplinarHistoricoTab;
